import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase, BidiText } from '../../../shared/components/common';
import { getEntityUrl } from '../../../shared/utils/entityUrls';
import { useJobs } from '../hooks/useJobs';
import { JobQuickCreateDialog } from './JobQuickCreateDialog';
import type { JobPosting } from '../services/recruitingApi';

interface JobPickerProps {
  /** Currently selected job ID */
  value: string;
  /** Callback when a job is selected */
  onChange: (jobId: string) => void;
  /** Optional label to display above the picker */
  label?: string;
  /** Placeholder text when no job is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the "None" option to clear selection */
  allowClear?: boolean;
  /** Whether the field is required (shows * on label) */
  required?: boolean;
  /** Job IDs to exclude from the list */
  excludeJobIds?: string[];
  /** Filter by job status (default: exclude closed/filled) */
  excludeStatuses?: string[];
  /** Hide the "Add new" action */
  hideAddNew?: boolean;
  /** Show selected job as a clickable link to its detail page */
  linkToEntity?: boolean;
}

export function JobPicker({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  className,
  allowClear = false,
  required = false,
  excludeJobIds = [],
  excludeStatuses = ['closed', 'filled'],
  hideAddNew = false,
  linkToEntity = false,
}: JobPickerProps) {
  const { t } = useTranslation('recruiting');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogTitle, setCreateDialogTitle] = useState('');

  const { data: jobsData } = useJobs({ search: search || undefined, limit: 50 });

  const jobs = useMemo(() => {
    const allJobs = jobsData?.data ?? [];
    return allJobs.filter(
      (job) =>
        !excludeStatuses.includes(job.status) &&
        !excludeJobIds.includes(job.id)
    );
  }, [jobsData?.data, excludeStatuses, excludeJobIds]);

  const handleSelect = useCallback((jobId: string) => {
    onChange(jobId);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleAddNew = useCallback(() => {
    setCreateDialogTitle(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleJobCreated = useCallback((job: JobPosting) => {
    onChange(job.id);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange]);

  const addNewLabel = search.trim()
    ? t('jobPicker.createJobWithName', { name: search.trim() })
    : t('jobPicker.addNewJob');

  return (
    <>
      <EntityPickerBase<JobPosting>
        value={value}
        items={jobs}
        getItemLabel={(job) => job.title}
        renderItem={(job, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <Briefcase className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate"><BidiText>{job.title}</BidiText></span>
            {job.department?.name && (
              <span className="ms-2 text-xs text-muted-foreground shrink-0">
                <BidiText>{job.department.name}</BidiText>
              </span>
            )}
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('jobPicker.searchOrCreate')}
        searchPlaceholder={t('jobPicker.searchJobs')}
        emptyMessage={t('jobPicker.noJobsFound')}
        disabled={disabled}
        className={className}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        hideAddNew={hideAddNew}
        addNewLabel={addNewLabel}
        onAddNew={handleAddNew}
        selectedEntityUrl={linkToEntity && value
          ? getEntityUrl({ entityType: 'recruiting_job_postings', entityId: value })
          : undefined}
        entityIcon={linkToEntity ? Briefcase : undefined}
      />

      <JobQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleJobCreated}
        skipNavigation
        defaultTitle={createDialogTitle}
      />
    </>
  );
}
